import {
  DEFAULT_GAMEPAD_TUNING,
  defaultGamepadMapping,
  shapeSteeringAxis,
  type GamepadAnalogBinding,
  type GamepadCaptureKind,
  type GamepadCaptureResult,
  type GamepadMapping,
  type GamepadStatus,
} from './game/gamepad.js';
import {
  buttonLabel,
  keyCapHtml,
  padActionGlyphHtml,
  padFamilyName,
  type PadAction,
  type PadFamily,
} from './input-glyphs.js';
import {
  KEY_ACTIONS,
  KEY_ACTION_LABELS,
  isReservedKeyCode,
  keyboardBindings,
  resetKeyboardBindings,
  setKeyboardBinding,
  type KeyAction,
} from './keyboard-bindings.js';
import type { GameSettings } from './settings.js';
import { t } from './i18n/index.js';

export interface ControlsSettingsHost {
  readonly root: HTMLElement;
  settings(): GameSettings;
  applySettings(): void;
  beginCapture(kind: GamepadCaptureKind): Promise<GamepadCaptureResult | null> | null;
  cancelCapture(): void;
  setMapping(mapping: GamepadMapping): void;
  resetMapping(): void;
  previewRumble(strength: number): void;
  inputFamily(): PadFamily;
  toast(message: string): void;
  /** Called after any binding change so HUD hints can re-render. */
  onBindingsChanged(): void;
}

interface PadBindingRow {
  id: PadAction;
  label: string;
  capture: GamepadCaptureKind | null;
  prompt: string;
}

const PAD_ROWS: readonly PadBindingRow[] = [
  { id: 'steer', label: 'STEER', capture: 'axis', prompt: 'PUSH THE STICK LEFT' },
  { id: 'throttle', label: 'ACCELERATE', capture: 'analog', prompt: 'SQUEEZE THE GAS TRIGGER' },
  { id: 'brake', label: 'BRAKE / REVERSE', capture: 'analog', prompt: 'SQUEEZE THE BRAKE TRIGGER' },
  { id: 'handbrake', label: 'DRIFT', capture: 'button', prompt: 'PRESS A BUTTON' },
  { id: 'nitro', label: 'NITRO', capture: 'button', prompt: 'PRESS A BUTTON' },
  { id: 'camera', label: 'CAMERA', capture: 'button', prompt: 'PRESS A BUTTON' },
  { id: 'reset', label: 'RECOVER', capture: 'button', prompt: 'PRESS A BUTTON' },
  { id: 'fire', label: 'FIRE (TANK)', capture: 'button', prompt: 'PRESS A BUTTON' },
  { id: 'turret', label: 'TURRET (TANK)', capture: 'axis', prompt: 'PUSH THE STICK LEFT' },
  { id: 'settings', label: 'PAUSE MENU', capture: 'button', prompt: 'PRESS A BUTTON' },
  { id: 'bank', label: 'BANK HEAT', capture: null, prompt: '' },
];

const BUTTON_FIELDS = ['handbrakeButton', 'nitroButton', 'fireButton', 'cameraButton', 'resetButton', 'settingsButton'] as const;
type ButtonField = typeof BUTTON_FIELDS[number];
const ACTION_FIELD: Partial<Record<PadAction, ButtonField>> = {
  handbrake: 'handbrakeButton',
  nitro: 'nitroButton',
  fire: 'fireButton',
  camera: 'cameraButton',
  reset: 'resetButton',
  settings: 'settingsButton',
};

const SETTINGS_TAB_STORAGE_KEY = 'nightshift:settings-tab';

/** Owns the settings tab strip and the CONTROLS tab. */
export class ControlsSettingsPanel {
  private status: GamepadStatus = { connected: false, index: null, name: '', standardMapping: false };
  private mapping: GamepadMapping = defaultGamepadMapping();
  private family: PadFamily = 'xbox';
  private renderedFamily: PadFamily | null = null;
  private view: 'gamepad' | 'keyboard' = 'gamepad';
  private userPickedView = false;
  private capturing: { row: string; cleanup: () => void } | null = null;
  private highlighted: PadAction | null = null;

  constructor(private readonly host: ControlsSettingsHost) {}

  bind(): void {
    const root = this.host.root;
    for (const tab of root.querySelectorAll<HTMLButtonElement>('[data-settings-tab]')) {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.settingsTab ?? 'video'));
    }
    for (const button of root.querySelectorAll<HTMLButtonElement>('[data-controls-view]')) {
      button.addEventListener('click', () => {
        this.userPickedView = true;
        this.setView(button.dataset.controlsView === 'keyboard' ? 'keyboard' : 'gamepad');
      });
    }
    this.bindRange('#gamepad-steer-dead-zone', (settings, value) => { settings.gamepadSteerDeadZone = value; });
    this.bindRange('#gamepad-steer-linearity', (settings, value) => { settings.gamepadSteerLinearity = value; });
    this.bindRange('#gamepad-trigger-dead-zone', (settings, value) => { settings.gamepadTriggerDeadZone = value; });
    this.bindRange('#gamepad-vibration', (settings, value) => { settings.gamepadVibration = value; });
    root.querySelector('#gamepad-vibration')?.addEventListener('change', () => this.host.previewRumble(0.6));
    root.querySelector('#gamepad-test-rumble')?.addEventListener('click', () => this.host.previewRumble(0.85));
    root.querySelector('#gamepad-reset-map')?.addEventListener('click', () => {
      this.cancelCapture();
      this.host.resetMapping();
      this.host.toast(t('Controller map reset to defaults'));
    });
    root.querySelector('#keyboard-reset-map')?.addEventListener('click', () => {
      this.cancelCapture();
      resetKeyboardBindings();
      this.renderKeyboardRows();
      this.host.onBindingsChanged();
      this.host.toast(t('Keyboard map reset to defaults'));
    });
    for (const id of ['#gamepad-steer-invert', '#gamepad-turret-invert']) {
      root.querySelector<HTMLInputElement>(id)?.addEventListener('change', () => {
        this.commitMapping({
          ...this.mapping,
          steerInverted: root.querySelector<HTMLInputElement>('#gamepad-steer-invert')?.checked ?? this.mapping.steerInverted,
          turretInverted: root.querySelector<HTMLInputElement>('#gamepad-turret-invert')?.checked ?? this.mapping.turretInverted,
        });
      });
    }
    const gamepadList = root.querySelector<HTMLElement>('#gamepad-bindings');
    gamepadList?.addEventListener('click', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-pad-row]');
      if (row && !row.disabled) void this.captureGamepad(row.dataset.padRow as PadAction);
    });
    const highlight = (event: Event): void => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-pad-row]');
      this.setHighlight(row ? row.dataset.padRow as PadAction : null);
    };
    gamepadList?.addEventListener('focusin', highlight);
    gamepadList?.addEventListener('pointerover', highlight);
    gamepadList?.addEventListener('pointerleave', () => this.setHighlight(null));
    gamepadList?.addEventListener('focusout', () => this.setHighlight(null));
    root.querySelector('#keyboard-bindings')?.addEventListener('click', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-key-row]');
      if (row) this.captureKeyboard(row.dataset.keyRow as KeyAction);
    });
    this.renderDiagram();
    this.renderGamepadRows();
    this.renderKeyboardRows();
    this.syncForm();
  }

  /** Called when the settings dialog opens. */
  open(): void {
    let tab = 'video';
    try {
      tab = localStorage.getItem(SETTINGS_TAB_STORAGE_KEY) ?? tab;
    } catch {
      // Defaults to VIDEO without storage.
    }
    if (!this.userPickedView) {
      const padInUse = this.status.connected || document.documentElement.dataset.inputMode === 'gamepad';
      this.setView(padInUse ? 'gamepad' : 'keyboard');
    }
    this.selectTab(tab, false);
    this.syncForm();
  }

  /** Called when the settings dialog closes. */
  close(): void {
    this.cancelCapture();
  }

  /** The first control a controller should land on when the dialog opens. */
  defaultFocus(): HTMLElement | null {
    return this.host.root.querySelector<HTMLElement>('.settings-tab[aria-selected="true"]');
  }

  selectTab(id: string, remember = true): void {
    const root = this.host.root;
    const tabs = [...root.querySelectorAll<HTMLButtonElement>('[data-settings-tab]')];
    const available = tabs.filter((tab) => tab.offsetParent !== null || tab.getClientRects().length > 0);
    const target = available.find((tab) => tab.dataset.settingsTab === id) ?? available[0];
    if (!target) return;
    const selected = target.dataset.settingsTab!;
    for (const tab of tabs) {
      const active = tab === target;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      tab.toggleAttribute('data-pad-default', active);
    }
    for (const panel of root.querySelectorAll<HTMLElement>('[data-settings-panel]')) {
      panel.hidden = panel.dataset.settingsPanel !== selected;
    }
    root.querySelector('.settings-card')?.setAttribute('data-tab', selected);
    root.querySelector('.settings-grid')?.scrollTo({ top: 0 });
    if (selected !== 'controls') this.cancelCapture();
    if (remember) {
      try {
        localStorage.setItem(SETTINGS_TAB_STORAGE_KEY, selected);
      } catch {
        // Session-only memory is fine.
      }
    }
  }

  updateStatus(status: GamepadStatus): void {
    const wasConnected = this.status.connected;
    this.status = status;
    if (status.family) this.family = status.family;
    else this.family = this.host.inputFamily();
    const mappingChanged = status.mapping && JSON.stringify(status.mapping) !== JSON.stringify(this.mapping);
    if (status.mapping && mappingChanged) this.mapping = status.mapping;
    const root = this.host.root;
    const card = root.querySelector<HTMLElement>('#gamepad-settings-status');
    const title = root.querySelector<HTMLElement>('#controls-device-title');
    const detail = root.querySelector<HTMLElement>('#controls-device-detail');
    if (card) card.dataset.status = status.connected ? 'connected' : 'waiting';
    if (title) {
      title.textContent = status.connected
        ? t('{family} CONTROLLER // {state}', {
          family: padFamilyName(this.family),
          state: status.mappingSource === 'custom' ? t('CUSTOM MAP') : status.standardMapping ? t('READY') : t('LEGACY MAP'),
        })
        : t('NO CONTROLLER DETECTED');
    }
    if (detail) {
      detail.textContent = status.connected
        ? `${status.name}${status.rumble === false ? ` · ${t('no vibration support in this browser')}` : ''}${!status.standardMapping && status.mappingSource !== 'custom' ? ` · ${t('rebind each action below to finish setup')}` : ''}`
        : t('Connect a controller and press any button. Wired and Bluetooth pads are detected automatically.');
    }
    const stage = root.querySelector<HTMLElement>('#controls-pad-stage');
    if (stage) stage.dataset.connected = String(status.connected);
    if (this.renderedFamily !== this.family) this.renderDiagram();
    if (mappingChanged || this.renderedFamily !== this.family || wasConnected !== status.connected) {
      this.renderGamepadRows();
      this.syncInvertToggles();
      this.host.onBindingsChanged();
    }
    if (!wasConnected && status.connected && !this.userPickedView) this.setView('gamepad');
    this.updateLive();
  }

  currentMapping(): GamepadMapping {
    return this.mapping;
  }

  syncForm(): void {
    const settings = this.host.settings();
    const set = (selector: string, value: number): void => {
      const input = this.host.root.querySelector<HTMLInputElement>(selector);
      if (input) input.value = String(value);
    };
    set('#gamepad-steer-dead-zone', settings.gamepadSteerDeadZone);
    set('#gamepad-steer-linearity', settings.gamepadSteerLinearity);
    set('#gamepad-trigger-dead-zone', settings.gamepadTriggerDeadZone);
    set('#gamepad-vibration', settings.gamepadVibration);
    this.syncLabels();
    this.syncInvertToggles();
  }

  cancelCapture(): void {
    if (!this.capturing) return;
    const { cleanup } = this.capturing;
    this.capturing = null;
    cleanup();
  }

  private setView(view: 'gamepad' | 'keyboard'): void {
    this.view = view;
    const root = this.host.root;
    for (const button of root.querySelectorAll<HTMLButtonElement>('[data-controls-view]')) {
      button.setAttribute('aria-pressed', String(button.dataset.controlsView === view));
    }
    for (const panel of root.querySelectorAll<HTMLElement>('[data-controls-view-panel]')) {
      panel.hidden = panel.dataset.controlsViewPanel !== view;
    }
    if (view !== 'gamepad') this.cancelCapture();
  }

  private bindRange(selector: string, apply: (settings: GameSettings, value: number) => void): void {
    this.host.root.querySelector<HTMLInputElement>(selector)?.addEventListener('input', (event) => {
      apply(this.host.settings(), Number((event.currentTarget as HTMLInputElement).value));
      this.syncLabels();
      this.host.applySettings();
      this.updateLive();
    });
  }

  private syncLabels(): void {
    const settings = this.host.settings();
    const set = (selector: string, text: string): void => {
      const element = this.host.root.querySelector(selector);
      if (element) element.textContent = text;
    };
    set('#gamepad-steer-dead-zone-value', `${Math.round(settings.gamepadSteerDeadZone * 100)}%`);
    const linearity = settings.gamepadSteerLinearity;
    set('#gamepad-steer-linearity-value', Math.abs(linearity - 1) < 0.03
      ? t('LINEAR')
      : linearity < 1 ? t('QUICK {value}', { value: linearity.toFixed(2) }) : t('PRECISE {value}', { value: linearity.toFixed(2) }));
    set('#gamepad-trigger-dead-zone-value', `${Math.round(settings.gamepadTriggerDeadZone * 100)}%`);
    set('#gamepad-vibration-value', settings.gamepadVibration <= 0 ? t('OFF') : `${Math.round(settings.gamepadVibration * 100)}%`);
    const deadZone = this.host.root.querySelector<HTMLElement>('#steer-deadzone');
    if (deadZone) deadZone.style.setProperty('--dead-zone', String(settings.gamepadSteerDeadZone));
  }

  private syncInvertToggles(): void {
    const steer = this.host.root.querySelector<HTMLInputElement>('#gamepad-steer-invert');
    const turret = this.host.root.querySelector<HTMLInputElement>('#gamepad-turret-invert');
    if (steer) steer.checked = this.mapping.steerInverted;
    if (turret) turret.checked = this.mapping.turretInverted;
  }

  private renderDiagram(): void {
    const container = this.host.root.querySelector<HTMLElement>('#controls-pad-svg');
    if (!container) return;
    this.renderedFamily = this.family;
    container.innerHTML = gamepadDiagramSvg(this.family);
    this.setHighlight(this.highlighted);
  }

  private renderGamepadRows(): void {
    const list = this.host.root.querySelector<HTMLElement>('#gamepad-bindings');
    if (!list) return;
    const disabled = !this.status.connected;
    const focusedRow = (document.activeElement as HTMLElement | null)?.dataset?.padRow;
    list.innerHTML = PAD_ROWS.map((row) => {
      const fixed = row.capture === null;
      return `<li><button type="button" class="binding-row${fixed ? ' fixed' : ''}" data-pad-row="${row.id}" data-pad-verb="${t('REBIND')}" ${disabled || fixed ? 'disabled' : ''}>
        <span class="binding-label">${t(row.label)}</span>
        <span class="binding-glyph">${padActionGlyphHtml(row.id, this.mapping, this.family)}</span>
        <small class="binding-hint">${fixed ? t('FIXED') : disabled ? t('CONNECT PAD') : t('REBIND')}</small>
      </button></li>`;
    }).join('');
    if (focusedRow) list.querySelector<HTMLElement>(`[data-pad-row="${focusedRow}"]`)?.focus({ preventScroll: true });
  }

  private renderKeyboardRows(): void {
    const list = this.host.root.querySelector<HTMLElement>('#keyboard-bindings');
    if (!list) return;
    const bindings = keyboardBindings();
    const focusedRow = (document.activeElement as HTMLElement | null)?.dataset?.keyRow;
    list.innerHTML = KEY_ACTIONS.map((action) => {
      const binding = bindings[action];
      return `<li><button type="button" class="binding-row" data-key-row="${action}" data-pad-verb="${t('REBIND')}">
        <span class="binding-label">${t(KEY_ACTION_LABELS[action])}</span>
        <span class="binding-glyph">${keyCapHtml(binding.primary)}${binding.secondary ? keyCapHtml(binding.secondary, 'secondary') : ''}</span>
        <small class="binding-hint">${t('REBIND')}</small>
      </button></li>`;
    }).join('');
    if (focusedRow) list.querySelector<HTMLElement>(`[data-key-row="${focusedRow}"]`)?.focus({ preventScroll: true });
  }

  private setHighlight(action: PadAction | null): void {
    this.highlighted = action;
    const svg = this.host.root.querySelector<SVGElement>('#controls-pad-svg svg');
    if (!svg) return;
    for (const element of svg.querySelectorAll('.highlight')) element.classList.remove('highlight');
    if (!action) return;
    for (const id of padElementsFor(action, this.mapping)) {
      svg.querySelector(`[data-pad-el="${id}"]`)?.classList.add('highlight');
    }
  }

  private startCaptureUi(rowSelector: string, message: string, cleanup: () => void): HTMLElement | null {
    this.cancelCapture();
    const row = this.host.root.querySelector<HTMLElement>(rowSelector);
    if (!row) return null;
    row.classList.add('capturing');
    const hint = row.querySelector<HTMLElement>('.binding-hint');
    if (hint) hint.textContent = t('ESC / WAIT TO CANCEL');
    const glyph = row.querySelector<HTMLElement>('.binding-glyph');
    if (glyph) glyph.innerHTML = `<em class="binding-capture">${message}<i></i></em>`;
    document.documentElement.dataset.padCapture = 'true';
    this.capturing = {
      row: rowSelector,
      cleanup: () => {
        delete document.documentElement.dataset.padCapture;
        row.classList.remove('capturing');
        cleanup();
      },
    };
    return row;
  }

  private async captureGamepad(action: PadAction): Promise<void> {
    const row = PAD_ROWS.find((candidate) => candidate.id === action);
    if (!row?.capture) return;
    const promise = this.host.beginCapture(row.capture);
    if (!promise) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.code !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.cancelCapture();
    };
    window.addEventListener('keydown', onKey, true);
    const rowSelector = `[data-pad-row="${action}"]`;
    this.startCaptureUi(rowSelector, t(row.prompt), () => {
      window.removeEventListener('keydown', onKey, true);
      this.host.cancelCapture();
      this.renderGamepadRows();
      this.host.root.querySelector<HTMLElement>(rowSelector)?.focus({ preventScroll: true });
    });
    const result = await promise;
    if (this.capturing?.row !== rowSelector) return;
    if (result) {
      const next = applyCapture(this.mapping, action, result);
      if (next) {
        this.commitMapping(next);
        this.host.previewRumble(0.35);
        this.host.toast(`${t(row.label)} → ${describeCapture(result, this.family)}`);
      }
    }
    this.cancelCapture();
  }

  private captureKeyboard(action: KeyAction): void {
    const rowSelector = `[data-key-row="${action}"]`;
    let timer = 0;
    const onKey = (event: KeyboardEvent): void => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.code === 'Escape') {
        this.cancelCapture();
        return;
      }
      if (isReservedKeyCode(event.code)) return;
      const result = setKeyboardBinding(action, event.code);
      this.cancelCapture();
      if (result) {
        this.renderKeyboardRows();
        this.host.onBindingsChanged();
        this.host.toast(`${t(KEY_ACTION_LABELS[action])} → ${keyCapText(event.code)}`);
      }
    };
    this.startCaptureUi(rowSelector, t('PRESS A KEY'), () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey, true);
      this.renderKeyboardRows();
      this.host.root.querySelector<HTMLElement>(rowSelector)?.focus({ preventScroll: true });
    });
    // Defer so the Enter/Space that activated the row is not captured.
    window.setTimeout(() => {
      if (this.capturing?.row === rowSelector) window.addEventListener('keydown', onKey, true);
    }, 0);
    timer = window.setTimeout(() => {
      if (this.capturing?.row === rowSelector) this.cancelCapture();
    }, 6_000);
  }

  private commitMapping(mapping: GamepadMapping): void {
    this.mapping = mapping;
    this.host.setMapping(mapping);
    this.renderGamepadRows();
    this.syncInvertToggles();
    this.host.onBindingsChanged();
  }

  /** Live diagram, steering meter and pedal bars from diagnostics. */
  private updateLive(): void {
    const root = this.host.root;
    const svg = root.querySelector<SVGElement>('#controls-pad-svg svg');
    const axes = this.status.liveAxes ?? [];
    const buttons = this.status.liveButtons ?? [];
    if (svg) {
      for (const element of svg.querySelectorAll<SVGElement>('[data-pad-el^="b"]')) {
        const index = Number(element.dataset.padEl!.slice(1));
        const value = buttons[index] ?? 0;
        element.classList.toggle('pressed', value > 0.5);
        const fill = element.querySelector<SVGElement>('.trigger-fill');
        if (fill) fill.style.transform = `scaleY(${value.toFixed(2)})`;
      }
      const moveStick = (id: string, x: number, y: number): void => {
        const thumb = svg.querySelector<SVGElement>(`[data-pad-el="${id}"] .thumb`);
        if (thumb) thumb.style.transform = `translate(${(x * 11).toFixed(1)}px, ${(y * 11).toFixed(1)}px)`;
      };
      moveStick('ls', axes[0] ?? 0, axes[1] ?? 0);
      moveStick('rs', axes[2] ?? 0, axes[3] ?? 0);
    }
    const settings = this.host.settings();
    const rawAxis = axes[this.mapping.steerAxis] ?? 0;
    const tuning = {
      ...DEFAULT_GAMEPAD_TUNING,
      steerDeadZone: settings.gamepadSteerDeadZone,
      steerLinearity: settings.gamepadSteerLinearity,
    };
    const output = shapeSteeringAxis(rawAxis, tuning);
    const raw = root.querySelector<HTMLElement>('#steer-raw');
    const out = root.querySelector<HTMLElement>('#steer-output');
    const value = root.querySelector<HTMLElement>('#live-steer-value');
    if (raw) raw.style.left = `${(50 + rawAxis * 50).toFixed(1)}%`;
    if (out) {
      out.style.left = `${(50 + Math.min(0, output) * 50).toFixed(1)}%`;
      out.style.width = `${(Math.abs(output) * 50).toFixed(1)}%`;
    }
    if (value) value.textContent = `${output < 0 ? 'L ' : output > 0 ? 'R ' : ''}${Math.round(Math.abs(output) * 100)}%`;
    const pedal = (binding: GamepadAnalogBinding): number => {
      const v = binding.source === 'button'
        ? buttons[binding.index] ?? 0
        : binding.source === 'axis-range'
          ? ((axes[binding.index] ?? -1) + 1) / 2
          : binding.source === 'axis-negative' ? Math.max(0, -(axes[binding.index] ?? 0)) : Math.max(0, axes[binding.index] ?? 0);
      const dz = settings.gamepadTriggerDeadZone;
      return v <= dz ? 0 : (v - dz) / (1 - dz);
    };
    const brake = root.querySelector<HTMLElement>('#live-brake');
    const throttle = root.querySelector<HTMLElement>('#live-throttle');
    if (brake) brake.style.transform = `scaleX(${pedal(this.mapping.brake).toFixed(2)})`;
    if (throttle) throttle.style.transform = `scaleX(${pedal(this.mapping.throttle).toFixed(2)})`;
  }
}

/** Applies a captured input to a mapping; button conflicts swap. */
export function applyCapture(mapping: GamepadMapping, action: PadAction, result: GamepadCaptureResult): GamepadMapping | null {
  const next: GamepadMapping = { ...mapping, throttle: { ...mapping.throttle }, brake: { ...mapping.brake } };
  if (action === 'steer' || action === 'turret') {
    if (result.kind !== 'axis') return null;
    // The prompt asks for LEFT, and left must read as +1 (see steeringFromButtons).
    const inverted = result.direction < 0;
    if (action === 'steer') {
      next.steerAxis = result.index;
      next.steerInverted = inverted;
    } else {
      next.turretAxis = result.index;
      next.turretInverted = inverted;
    }
    return next;
  }
  if (action === 'throttle' || action === 'brake') {
    const binding: GamepadAnalogBinding = result.kind === 'button'
      ? { source: 'button', index: result.index }
      : {
          source: result.resting < -0.5 ? 'axis-range' : result.direction > 0 ? 'axis-positive' : 'axis-negative',
          index: result.index,
        };
    const other = action === 'throttle' ? 'brake' : 'throttle';
    if (sameAnalog(next[other], binding)) next[other] = { ...next[action] };
    if (binding.source === 'button') swapButtonOwner(next, binding.index, analogButton(next[action]));
    next[action] = binding;
    return next;
  }
  const field = ACTION_FIELD[action];
  if (!field || result.kind !== 'button') return null;
  const previous = next[field];
  swapButtonOwner(next, result.index, previous, field);
  if (next.throttle.source === 'button' && next.throttle.index === result.index) next.throttle = { source: 'button', index: previous };
  if (next.brake.source === 'button' && next.brake.index === result.index) next.brake = { source: 'button', index: previous };
  next[field] = result.index;
  return next;
}

function analogButton(binding: GamepadAnalogBinding): number | null {
  return binding.source === 'button' ? binding.index : null;
}

function swapButtonOwner(mapping: GamepadMapping, index: number, previous: number | null, except?: ButtonField): void {
  for (const field of BUTTON_FIELDS) {
    if (field === except || mapping[field] !== index) continue;
    if (previous !== null) mapping[field] = previous;
  }
}

function sameAnalog(a: GamepadAnalogBinding, b: GamepadAnalogBinding): boolean {
  return a.source === b.source && a.index === b.index;
}

function describeCapture(result: GamepadCaptureResult, family: PadFamily): string {
  if (result.kind === 'button') return buttonLabel(result.index, family);
  if (result.index <= 1) return t('LEFT STICK');
  if (result.index <= 3) return t('RIGHT STICK');
  return t('AXIS {n}', { n: result.index });
}

function keyCapText(code: string): string {
  return keyCapHtml(code).replace(/<[^>]+>/g, '');
}

function padElementsFor(action: PadAction, mapping: GamepadMapping): string[] {
  const axisElement = (axis: number): string => axis <= 1 ? 'ls' : axis <= 3 ? 'rs' : '';
  const analog = (binding: GamepadAnalogBinding): string => binding.source === 'button' ? `b${binding.index}` : '';
  switch (action) {
    case 'steer': return [axisElement(mapping.steerAxis), 'b14', 'b15'];
    case 'turret': return [axisElement(mapping.turretAxis), 'b4', 'b5'];
    case 'throttle': return [analog(mapping.throttle), 'b12'];
    case 'brake': return [analog(mapping.brake), 'b13'];
    case 'handbrake': return [`b${mapping.handbrakeButton}`];
    case 'nitro': return [`b${mapping.nitroButton}`];
    case 'fire': return [`b${mapping.fireButton}`];
    case 'camera': return [`b${mapping.cameraButton}`];
    case 'reset': return [`b${mapping.resetButton}`];
    case 'settings': return [`b${mapping.settingsButton}`];
    case 'bank': return ['b10'];
  }
}

/**
 * A stylised controller in the race UI's line language. PlayStation pads put
 * the D-pad above the left stick; every other family uses the offset layout.
 */
export function gamepadDiagramSvg(family: PadFamily): string {
  const offset = family !== 'playstation';
  const leftStick = offset ? { x: 112, y: 98 } : { x: 146, y: 146 };
  const dpad = offset ? { x: 146, y: 146 } : { x: 112, y: 98 };
  const rightStick = { x: 234, y: 146 };
  const face = { x: 268, y: 98 };
  const faceButton = (index: number, x: number, y: number): string => {
    const label = buttonLabel(index, family);
    return `<g class="pad-face" data-pad-el="b${index}" data-slot="${['south', 'east', 'west', 'north'][index]}"><circle cx="${x}" cy="${y}" r="11"/><text x="${x}" y="${y + 0.5}">${label}</text></g>`;
  };
  const stick = (id: string, index: number, x: number, y: number): string => (
    `<g class="pad-stick" data-pad-el="${id}"><circle class="well" cx="${x}" cy="${y}" r="21"/><g class="thumb-wrap" data-pad-el="b${index}"><g class="thumb"><circle cx="${x}" cy="${y}" r="13.5"/><circle class="grip" cx="${x}" cy="${y}" r="8"/></g></g></g>`
  );
  const dpadArm = (index: number, d: string): string => `<path class="pad-dpad" data-pad-el="b${index}" d="${d}"/>`;
  const dx = dpad.x;
  const dy = dpad.y;
  const trigger = (index: number, x: number): string => (
    `<g class="pad-trigger" data-pad-el="b${index}"><rect class="trigger-body" x="${x}" y="8" width="54" height="22" rx="8"/><rect class="trigger-fill" x="${x}" y="8" width="54" height="22" rx="8"/><text x="${x + 27}" y="20">${buttonLabel(index, family)}</text></g>`
  );
  const bumper = (index: number, x: number): string => (
    `<g class="pad-bumper" data-pad-el="b${index}"><rect x="${x}" y="34" width="66" height="11" rx="5.5"/><text x="${x + 33}" y="40">${buttonLabel(index, family)}</text></g>`
  );
  return `<svg viewBox="0 0 380 236" role="img" aria-label="${t('{family} controller layout', { family: padFamilyName(family) })}">
    <defs>
      <linearGradient id="pad-body-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="var(--pad-body-top)"/><stop offset="1" stop-color="var(--pad-body-bottom)"/></linearGradient>
    </defs>
    ${trigger(6, 74)}${trigger(7, 252)}
    ${bumper(4, 68)}${bumper(5, 246)}
    <path class="pad-body" d="M120 50 C160 43 220 43 260 50 L286 54 C318 60 338 84 348 118 L366 184 C374 212 352 232 328 226 C310 221 300 206 286 188 L268 166 C248 156 132 156 112 166 L94 188 C80 206 70 221 52 226 C28 232 6 212 14 184 L32 118 C42 84 62 60 94 54 Z"/>
    <path class="pad-body-line" d="M126 62 C164 56 216 56 254 62"/>
    ${stick('ls', 10, leftStick.x, leftStick.y)}
    ${stick('rs', 11, rightStick.x, rightStick.y)}
    <g class="pad-dpad-group">
      <circle class="well" cx="${dx}" cy="${dy}" r="22"/>
      ${dpadArm(12, `M${dx - 6} ${dy - 6} V${dy - 18} a2 2 0 0 1 2 -2 h8 a2 2 0 0 1 2 2 V${dy - 6} Z`)}
      ${dpadArm(13, `M${dx - 6} ${dy + 6} V${dy + 18} a2 2 0 0 0 2 2 h8 a2 2 0 0 0 2 -2 V${dy + 6} Z`)}
      ${dpadArm(14, `M${dx - 6} ${dy - 6} H${dx - 18} a2 2 0 0 0 -2 2 v8 a2 2 0 0 0 2 2 H${dx - 6} Z`)}
      ${dpadArm(15, `M${dx + 6} ${dy - 6} H${dx + 18} a2 2 0 0 1 2 2 v8 a2 2 0 0 1 -2 2 H${dx + 6} Z`)}
    </g>
    ${faceButton(0, face.x, face.y + 20)}${faceButton(1, face.x + 20, face.y)}${faceButton(2, face.x - 20, face.y)}${faceButton(3, face.x, face.y - 20)}
    <g class="pad-system" data-pad-el="b8"><rect x="160" y="88" width="20" height="11" rx="5.5"/></g>
    <g class="pad-system" data-pad-el="b9"><rect x="200" y="88" width="20" height="11" rx="5.5"/></g>
    <g class="pad-system pad-home" data-pad-el="b16"><circle cx="190" cy="70" r="9"/></g>
  </svg>`;
}

